import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import BarcodeScannerCore from './BarcodeScannerCore';

// Use the reader-only WASM build (~911 KB) for faster load and better accuracy
// than the pure-JS @zxing/browser engine.
import { readBarcodes } from 'zxing-wasm/reader';

const PREFERRED_CAMERA_KEY = 'preferredCameraId';

// Decode interval in ms — with ROI cropping + WASM, each decode is fast so
// 300 ms gives a good balance between responsiveness and CPU usage.
const DECODE_INTERVAL_MS = 300;

/**
 * Score cameras so that the standard (non-wide) back camera wins.
 * Ultra-wide lenses spread pixels over a larger FOV, giving fewer pixels on
 * the barcode — bad for scanning.
 */
const chooseBestCamera = (devices) => {
  const scored = devices.map((device) => {
    const label = (device.label || '').toLowerCase();
    let score = 0;

    // Prefer back / rear / environment-facing cameras
    if (label.includes('back') || label.includes('rear')) score += 10;
    if (label.includes('environment')) score += 8;

    // Penalize ultra-wide and wide — they reduce effective resolution on the barcode
    if (label.includes('ultra') && label.includes('wide')) score -= 20;
    else if (label.includes('wide')) score -= 10;

    // Slight preference for non-wide (standard lens)
    if (!label.includes('wide') && !label.includes('ultra')) score += 5;

    // Tiebreaker: device labelled "0" is usually the primary sensor
    if (label.includes('0')) score += 1;

    return { device, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.length > 0 ? scored[0].device : devices[0];
};

/**
 * Normalise a raw barcode string into a valid 17-character VIN.
 * Returns null if the string is not a plausible VIN.
 */
const normalizeVinCandidate = (raw) => {
  if (!raw) return null;

  const cleaned = String(raw).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Handle the occasional leading 'I' that some 2D barcodes include
  if (cleaned.length === 18 && cleaned.startsWith('I')) {
    const trimmed = cleaned.slice(1);
    if (trimmed.length === 17 && !/[IOQ]/.test(trimmed) && /\d/.test(trimmed)) {
      return trimmed;
    }
  }

  if (cleaned.length !== 17) return null;

  // Disallow I, O, Q in VINs (ISO 3779)
  if (/[IOQ]/.test(cleaned)) return null;

  // Must contain at least 1 digit
  if (!/\d/.test(cleaned)) return null;

  return cleaned;
};

export default function BarcodeScannerWrapper({ show, onBarcodeDetected, onClose, beepRef }) {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(document.createElement('canvas'));
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);

  // Reader options for zxing-wasm — configured once.
  const readerOptions = useMemo(() => ({
    // VIN barcodes: Code 39 (most common 1D), Code 128 (alternative 1D),
    // PDF417 (2D on newer windshields), DataMatrix (some OEMs)
    formats: ['Code39', 'Code128', 'PDF417', 'DataMatrix'],
    tryHarder: true,
    tryRotate: true,
    tryInvert: false,      // VINs are always standard reflectance
    tryDownscale: true,
    maxNumberOfSymbols: 1, // We only expect one VIN barcode at a time
    binarizer: 'LocalAverage',
  }), []);

  // ── 1) Enumerate cameras and pick the best one ──────────────────────────
  useEffect(() => {
    if (!show) return;

    let mounted = true;

    (async () => {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');

      if (!mounted) return;

      // Prefer back-facing cameras
      const back = cams.filter(c => !/front|user/i.test(c.label || ''));
      const list = back.length ? back : cams;

      setDevices(list);

      const saved = localStorage.getItem(PREFERRED_CAMERA_KEY);
      const found = list.find(d => d.deviceId === saved);
      const best = found || chooseBestCamera(list) || list[0];

      setDeviceId(best?.deviceId || null);

      if (best?.deviceId) {
        localStorage.setItem(PREFERRED_CAMERA_KEY, best.deviceId);
      }
    })();

    return () => { mounted = false; };
  }, [show]);

  // ── Helper: stop the active camera stream ───────────────────────────────
  const stopStream = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      const stream = streamRef.current;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach(t => t.stop());
      }
    } catch { /* ignore */ }

    streamRef.current = null;

    try {
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch { /* ignore */ }

    setTorchOn(false);
    setTorchSupported(false);
  }, []);

  // ── 2) Start camera + decode loop ──────────────────────────────────────
  useEffect(() => {
    if (!show || !deviceId) return;

    stoppedRef.current = false;

    const startScanning = async () => {
      // Stop any previous session
      stopStream();

      if (stoppedRef.current) return;

      // Request HD resolution with continuous autofocus — critical for
      // close-up barcode scanning.  The browser will negotiate the closest
      // supported configuration.
      const constraints = {
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          focusMode: { ideal: 'continuous' },
          // Some Android browsers recognise these advanced constraints:
          advanced: [
            { focusMode: 'continuous' },
            { zoom: 1.0 },
          ],
        },
        audio: false,
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch {
        // Fallback: try without resolution constraints
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: deviceId } },
            audio: false,
          });
        } catch (e) {
          console.error('Failed to start camera:', e);
          return;
        }
      }

      if (stoppedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      streamRef.current = stream;

      // Check for torch (flashlight) support
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const caps = videoTrack.getCapabilities?.();
          if (caps && caps.torch) {
            setTorchSupported(true);
          }
        } catch { /* torch detection not supported */ }
      }

      // Attach stream to video element
      const video = videoRef.current;
      if (!video) return;

      video.srcObject = stream;
      await video.play().catch(() => {});

      // Wait a moment for the camera to stabilise / autofocus to engage
      await new Promise(r => setTimeout(r, 500));

      if (stoppedRef.current) return;

      // ── Decode loop ──────────────────────────────────────────────────
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      timerRef.current = setInterval(async () => {
        if (stoppedRef.current || !video.videoWidth || video.paused) return;

        // Crop to the ROI area (central horizontal band) before decoding.
        // This matches the green box shown to the user:
        //   top: 40%, height: 20% of video, left: 5%, width: 90%
        const vw = video.videoWidth;
        const vh = video.videoHeight;

        const roiX = Math.round(vw * 0.05);
        const roiY = Math.round(vh * 0.35);  // slightly above 40% to give padding
        const roiW = Math.round(vw * 0.90);
        const roiH = Math.round(vh * 0.30);  // slightly taller than 20% for tolerance

        canvas.width = roiW;
        canvas.height = roiH;

        ctx.drawImage(video, roiX, roiY, roiW, roiH, 0, 0, roiW, roiH);

        const imageData = ctx.getImageData(0, 0, roiW, roiH);

        try {
          const results = await readBarcodes(imageData, readerOptions);

          if (stoppedRef.current) return;

          for (const result of results) {
            if (!result.isValid) continue;

            const vin = normalizeVinCandidate(result.text);
            if (!vin) continue;

            // Found a valid VIN — stop immediately
            stoppedRef.current = true;

            beepRef?.current?.play().catch(() => {});

            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }

            onBarcodeDetected(vin);
            onClose();
            return;
          }
        } catch (e) {
          // Decode errors are expected (no barcode in frame) — keep scanning
          // console.debug('decode error', e);
        }
      }, DECODE_INTERVAL_MS);
    };

    startScanning();

    return () => {
      stoppedRef.current = true;
      stopStream();
    };
  }, [show, deviceId, readerOptions, onBarcodeDetected, onClose, beepRef, stopStream]);

  // ── Torch toggle ────────────────────────────────────────────────────────
  const handleToggleTorch = useCallback(async () => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    if (!track) return;

    const newState = !torchOn;

    try {
      await track.applyConstraints({ advanced: [{ torch: newState }] });
      setTorchOn(newState);
    } catch (e) {
      console.warn('Torch toggle failed:', e);
    }
  }, [torchOn]);

  // ── Camera selection ────────────────────────────────────────────────────
  const handleSelect = (e) => {
    const id = e.target.value;
    setDeviceId(id);
    localStorage.setItem(PREFERRED_CAMERA_KEY, id);
  };

  if (!show || !deviceId) return null;

  return ReactDOM.createPortal(
    <BarcodeScannerCore
      videoRef={videoRef}
      videoDevices={devices}
      selectedDeviceId={deviceId}
      onSelectDevice={handleSelect}
      onClose={() => {
        stoppedRef.current = true;
        stopStream();
        onClose();
      }}
      beepRef={beepRef}
      torchSupported={torchSupported}
      torchOn={torchOn}
      onToggleTorch={handleToggleTorch}
    />,
    document.getElementById('scanner-portal-root')
  );
}
